import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://manager.tsk-arena.com';

// Export l'URL de base de l'API pour les composants qui en ont besoin
export const API_BASE_URL = `${API_URL}/api/v1`;

// Export l'URL de base pour les fichiers statiques (avatars, etc.)
export const getStaticUrl = (path) => {
  if (!path) return null;
  return `${API_URL}${path}`;
};

const apiClient = axios.create({
  baseURL: `${API_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Intercepteur pour ajouter le token JWT à chaque requête
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs d'authentification
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expiré ou invalide, déconnecter l'utilisateur
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const usersApi = {
  getAll: (params) => apiClient.get('/auth/users', { params }),
  getMe: () => apiClient.get('/auth/me'),
  update: (id, data) => apiClient.put(`/auth/users/${id}`, data),
  updatePassword: (id, data) => apiClient.put(`/auth/users/${id}/password`, data),
  uploadAvatar: (userId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/auth/users/${userId}/avatar`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

export const teamsApi = {
  getAll: (params) => apiClient.get('/teams/', { params }),
  getWithStats: (params) => apiClient.get('/teams/with-stats', { params }),
  getById: (id) => apiClient.get(`/teams/${id}`),
  create: (data) => apiClient.post('/teams/', data),
  update: (id, data) => apiClient.put(`/teams/${id}`, data),
  delete: (id) => apiClient.delete(`/teams/${id}`),
  uploadLogo: (teamId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/teams/${teamId}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteLogo: (teamId) => apiClient.delete(`/teams/${teamId}/logo`),
};

export const teamMembersApi = {
  getTeamMembers: (teamId) => apiClient.get(`/team-members/team/${teamId}`),
  getUserTeams: (userId) => apiClient.get(`/team-members/user/${userId}/teams`),
  addMember: (teamId, userId, teamRole) => apiClient.post(`/team-members/team/${teamId}/members`, { user_id: userId, team_role: teamRole }),
  removeMember: (teamId, userId) => apiClient.delete(`/team-members/team/${teamId}/members/${userId}`),
  updateMemberMMR: (teamId, userId, mmr, mmrActive) => apiClient.put(`/team-members/team/${teamId}/members/${userId}/mmr`, { mmr, mmr_active: mmrActive }),
};

export const gamesApi = {
  getAll: () => apiClient.get('/games/'),
  create: (data) => apiClient.post('/games/', data),
  update: (id, data) => apiClient.put(`/games/${id}`, data),
  delete: (id) => apiClient.delete(`/games/${id}`),
  uploadLogo: (gameId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/games/${gameId}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteLogo: (gameId) => apiClient.delete(`/games/${gameId}/logo`),
};

export const eventsApi = {
  getAll: (params) => apiClient.get('/events/', { params }),
  getById: (id) => apiClient.get(`/events/${id}`),
  create: (data) => apiClient.post('/events/', data),
  update: (id, data) => apiClient.put(`/events/${id}`, data),
  delete: (id) => apiClient.delete(`/events/${id}`),
};

export const availabilityApi = {
  getTeamAvailability: (teamId, params) => apiClient.get(`/availability/team/${teamId}`, { params }),
  getPlayerAvailability: (playerId, params) => apiClient.get(`/availability/player/${playerId}`, { params }),
  create: (data) => apiClient.post('/availability', data),
  createBulk: (data) => apiClient.post('/availability/bulk', data),
  delete: (id) => apiClient.delete(`/availability/${id}`),
  deletePlayerDate: (playerId, date) => apiClient.delete(`/availability/player/${playerId}/date/${date}`),
  syncPlayerDay: (playerId, date, ranges) => apiClient.post('/availability/sync', {
    player_id: playerId,
    date: date,
    ranges: ranges
  }),
};

export const adminApi = {
  getAllUsers: () => apiClient.get('/admin/users'),
  updateUserRole: (userId, role, gameIds = []) => apiClient.put(`/admin/users/${userId}/role`, { role, game_ids: gameIds }),
  updateUserMmr: (userId, mmr) => apiClient.put(`/admin/users/${userId}/mmr`, { mmr }),
  updateResponsableGames: (userId, gameIds) => apiClient.put(`/admin/users/${userId}/responsable-games`, { game_ids: gameIds }),
  uploadAvatar: (userId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/admin/users/${userId}/avatar`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteUser: (userId) => apiClient.delete(`/admin/users/${userId}`),
};

export const notificationsApi = {
  getAll: (unreadOnly = false, limit = 50) => apiClient.get('/notifications/', { params: { unread_only: unreadOnly, limit } }),
  getUnreadCount: () => apiClient.get('/notifications/unread-count'),
  markAsRead: (notificationId) => apiClient.patch(`/notifications/${notificationId}/read`),
  markAllAsRead: () => apiClient.post('/notifications/mark-all-read'),
  delete: (notificationId) => apiClient.delete(`/notifications/${notificationId}`),
  deleteAll: () => apiClient.post('/notifications/delete-all'),
};

export const eventCommentsApi = {
  getAll: (eventId) => apiClient.get(`/events/${eventId}/comments`),
  create: (eventId, content, parentId = null) => apiClient.post(`/events/${eventId}/comments`, { event_id: eventId, content, parent_id: parentId }),
  update: (commentId, content) => apiClient.put(`/comments/${commentId}`, { content }),
  delete: (commentId) => apiClient.delete(`/comments/${commentId}`),
};

export const eventAttendancesApi = {
  getAll: (eventId) => apiClient.get(`/events/${eventId}/attendances`),
  set: (eventId, status) => apiClient.post(`/events/${eventId}/attendances`, { event_id: eventId, status }),
  remove: (eventId) => apiClient.delete(`/events/${eventId}/attendances`),
};

export const eventMediasApi = {
  getAll: (eventId, mediaType = null) => apiClient.get(`/events/${eventId}/medias`, { params: mediaType ? { media_type: mediaType } : {} }),
  upload: (eventId, file, mediaType, description = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('media_type', mediaType);
    if (description) formData.append('description', description);
    return apiClient.post(`/events/${eventId}/medias`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  delete: (mediaId) => apiClient.delete(`/medias/${mediaId}`),
};

export const permissionsApi = {
  getAll: () => apiClient.get('/permissions/'),
  update: (changes) => apiClient.put('/permissions/', { changes }),
};

export const rolesApi = {
  getAll: () => apiClient.get('/roles/'),
  create: (data) => apiClient.post('/roles/', data),
  update: (id, data) => apiClient.put(`/roles/${id}`, data),
  delete: (id) => apiClient.delete(`/roles/${id}`),
};

export const invitationsApi = {
  getAll: () => apiClient.get('/invitations/'),
  create: () => apiClient.post('/invitations/'),
  validate: (token) => apiClient.get(`/invitations/validate/${token}`),
  register: (token, username, password) => apiClient.post('/invitations/register', { token, username, password }),
  delete: (invitationId) => apiClient.delete(`/invitations/${invitationId}`),
};

export default apiClient;
